import { CoObjectItem } from "../interfaces/CoObject";
import React, { ReactNode, useEffect } from "react";
import { extractSelectedContentControl } from "../../office/DocumentManager";
import { addContentControlCustomXml, getCustomXmlOrThrow } from "../../utils/helpers/CustomXmlHelper";
import { formatMessage, formatMessageWithParams } from "../../utils/helpers/LanguageHelper";
import { OperationCompletedPanel } from "../layout/OperationCompletedPanel";
import { CoObjectTypeEnum } from "../../api/models";
import { PrimaryButton } from "@fluentui/react/lib/Button";
import { cleanDocumentFromCC, isSubPartContentControl } from "../../office/ContentControl";
import { CustomModal, CustomModalProps } from "../layout/ActionModal";
import LoadingManager from "../../common/loading/LoadingManager";
import { SELECT_COOBJECTS_NOTIFICATION } from "../../utils/constants/notification";
import { ButtonVariants } from "../../styles/fluent/ButtonVariants";
import FooterPanel from "../layout/FooterPanel";
import { inheritComponentData } from "../../utils/helpers/ComponentInstanceHelper";
import { generateMissingXML } from "../../redux/feature/cpm/map-xmlObjects";
import { getActiveObjectForContentControlId } from "../../api/object/CoObjectGeneral";

/* global console */

export interface CoObjectSelectorProps {
  documentId: string;
  children: (coObject: CoObjectItem) => ReactNode;
}

interface CoObjectSelectorState {
  failed: boolean;
  contentControlId?: string;
  coObject?: CoObjectItem;
  isContentControlDeletable: boolean;
  deleteConfirm: boolean;
  deleted: boolean;
}

const coObjectSelectorInitialState = {
  failed: false,
  isContentControlDeletable: false,
  deleteConfirm: false,
  deleted: false,
};

export const CoObjectSelector: React.FunctionComponent<CoObjectSelectorProps> = (props) => {
  const [state, setState] = React.useState<CoObjectSelectorState>(coObjectSelectorInitialState);
  const { failed, coObject, isContentControlDeletable, deleteConfirm, contentControlId, deleted } = state;
  const deleteConfirmModalProps = (contentControlId: string) => {
    return {
      modalTitle: formatMessageWithParams("cof.createnewobject.modal.delete", {
        coObjectName: "",
      }),
      messageTitle: "Are you sure you want to Delete document content?",
      message: "Are you sure you want to Delete document content?",
      type: "delete",
      onHide: () => {
        return;
      },
      actions: [
        {
          title: formatMessage("cof.base.noconfirmdelete"),
          onClick: () => {
            setState({ ...state, deleteConfirm: false });
          },
          variant: "blue",
        },
        {
          title: formatMessage("cof.base.confirmdelete"),
          onClick: async () => {
            await cleanDocumentFromCC(contentControlId);
            setState({
              ...state,
              deleteConfirm: false,
              deleted: true,
              isContentControlDeletable: false,
              contentControlId: undefined,
            });
          },
          variant: "red_filled",
        },
      ],
    } as CustomModalProps;
  };

  useEffect(() => {
    LoadingManager.create(SELECT_COOBJECTS_NOTIFICATION);
    extractSelectedContentControl()
      .then(async (contentControlId) => {
        console.log(`recupero da CustomXML per cc ${contentControlId}`);
        if (contentControlId) {
          const ccIsDeletable = !(await isSubPartContentControl(contentControlId));
          getCustomXmlOrThrow(contentControlId)
            .then(async (coObjectFromXML: CoObjectItem) => {
              console.log("coObject", coObjectFromXML);
              if (coObjectFromXML != null) {
                if (coObjectFromXML.info?.componentReference) {
                  if (coObjectFromXML.info?.componentReference.componentLogicId?.length) {
                    const inheritedCoObject = await inheritComponentData(coObjectFromXML);
                    setState({ ...state, coObject: inheritedCoObject, contentControlId: contentControlId });
                  } else
                    setState({
                      ...state,
                      coObject: null,
                      contentControlId: contentControlId,
                      isContentControlDeletable: true,
                      failed: true,
                    });
                } else {
                  setState({ ...state, coObject: coObjectFromXML, contentControlId: contentControlId });
                }
              } else {
                console.warn("No XML trying to regenerate from the DB... ");
                const generatedXML = await handleFailedXMLRetrieval(props.documentId, contentControlId);
                if (generatedXML) {
                  setState({ ...state, coObject: generatedXML, contentControlId: contentControlId });
                } else {
                  console.warn(`No XML found for the selected contentControl id (${contentControlId})`);
                  setState({
                    ...state,
                    failed: true,
                    isContentControlDeletable: ccIsDeletable,
                    contentControlId: contentControlId,
                  });
                }
              }
            })
            .catch((e) => {
              console.error(`error getting xml for contentControlId ${contentControlId}`, e);
              setState({
                ...state,
                failed: true,
                isContentControlDeletable: ccIsDeletable,
                contentControlId: contentControlId,
              });
            });
        } else {
          console.warn("No contentControl in the user selected range");
          setState({ ...state, failed: true });
        }
      })
      .catch((e) => {
        console.log("error contentControlId ", e);
        setState({ ...state, failed: true });
      })
      .finally(() => {
        LoadingManager.remove(SELECT_COOBJECTS_NOTIFICATION);
      });
  }, []);

  const handleFailedXMLRetrieval = async (documentId: string, contentControlId: string) => {
    LoadingManager.create("XML_REGEN");
    try {
      const serverCoObject = await getActiveObjectForContentControlId(documentId, contentControlId);
      const generatedXML = await generateMissingXML(serverCoObject);
      if (generatedXML) {
        await addContentControlCustomXml(contentControlId, generatedXML);
        return generatedXML;
      }
      return null;
    } catch (e) {
      console.error("XML Regeneration from DB error: ", e);
    } finally {
      LoadingManager.remove("XML_REGEN");
    }
  };

  return (
    <>
      {deleteConfirm && contentControlId && <CustomModal {...deleteConfirmModalProps(contentControlId)} />}
      {!coObject && !failed ? null : (
        <>
          {coObject && coObject.info.objectType != CoObjectTypeEnum.DOCUMENT_PART_INCLUDE ? (
            <>{props.children(coObject)}</>
          ) : (
            <>
              {isContentControlDeletable && !coObject ? (
                <OperationCompletedPanel
                  title={formatMessage("cof.base.edit")}
                  message={formatMessage("cof.base.confirmation.deleteinvalidobject")}
                  alt={formatMessage("cof.base.confirmation.deleteinvalidobject")}
                  icon="/assets/noObject.svg"
                >
                  <FooterPanel>
                    <PrimaryButton
                      id={"delete"}
                      text={formatMessage("cof.base.delete").toUpperCase()}
                      onClick={() => setState({ ...state, deleteConfirm: true })}
                      className={ButtonVariants.red_filled}
                      style={{ float: "left" }}
                      data-testid="deletecc"
                    />
                  </FooterPanel>
                </OperationCompletedPanel>
              ) : (
                <>
                  {deleted ? (
                    <OperationCompletedPanel
                      message={formatMessage("cof.createnewobject.modal.successmessage")}
                      alt={formatMessage("cof.createnewobject.modal.successmessage")}
                      icon="/assets/trash-3x.png"
                    />
                  ) : (
                    <OperationCompletedPanel
                      title={formatMessage("cof.base.edit")}
                      message={formatMessage("cof.objects.edit.message.anyfindeditableobject")}
                      alt={formatMessage("cof.objects.edit.message.anyfindeditableobject")}
                      icon="/assets/noObject.svg"
                    />
                  )}
                </>
              )}
            </>
          )}
        </>
      )}
    </>
  );
};
